import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import type {DailyStockTakeItem, DailyStockTakeItemId} from './buildDailyStockTakeDetailView';

/**
 * Thunks rather than plain strings: a module-scope `i18n.t` would run before the language is
 * initialised and freeze the English copy into the bundle.
 */
const ITEM_LABELS: Record<DailyStockTakeItemId, () => string> = {
  dailyReceipts: () => i18n.t('entity.warehouse.reporting.dailyReceipts'),
  dailyIssues: () => i18n.t('entity.warehouse.reporting.dailyIssues'),
  corrections: () => i18n.t('entity.warehouse.reporting.correctionsAndAdjustments'),
  receiptsFromSuppliers: () => i18n.t('entity.warehouse.reporting.receiptsFromSuppliers'),
  transfersToWarehouse: () => i18n.t('entity.warehouse.reporting.transfersToWarehouse'),
  materialReturns: () => i18n.t('entity.warehouse.reporting.materialReturns'),
  issuesToOrders: () => i18n.t('entity.warehouse.reporting.issuesToOrders'),
  transfersFromWarehouse: () => i18n.t('entity.warehouse.reporting.transfersFromWarehouse'),
};

const COUNT_SEPARATOR = ' · ';

/**
 * The documents and cards behind one line, as the one caption the design puts under the amount.
 * Either count can be absent on its own, so the parts are collected and only then joined —
 * a fixed "Documents: — · Articles: 2" would report an absence the closing job never stated.
 *
 * `count` must be a number: i18next skips plural resolution for a string, which would leave
 * these keys unable to ever gain the Czech, Slovak and Polish plural forms. The `?? 0` only
 * satisfies the type; each part is dropped when its count is absent.
 */
export function formatDailyStockTakeItem(item: DailyStockTakeItem) {
  const parts = [
    isNotNil(item.documents)
      ? i18n.t('entity.warehouse.reporting.documentsLine', {count: item.documents ?? 0})
      : null,
    isNotNil(item.articles)
      ? i18n.t('entity.warehouse.reporting.articlesLine', {count: item.articles ?? 0})
      : null,
  ].filter(isNotNil);

  return {label: ITEM_LABELS[item.id](), note: parts.join(COUNT_SEPARATOR)};
}
